/**
 * Admin settings API client — wraps `/v1/admin/preferences/*` and reuses the
 * shared request helper from `lib/api` for auth + error handling.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface AppearanceSettings {
  themeMode: ThemeMode;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  borderRadius: number;
  fontFamily: string;
  fontSize: number;
  compactMode: boolean;
  cardStyle: 'glass' | 'flat' | 'elevated';
  animationsEnabled: boolean;
}

export interface LayoutSettings {
  sidebarCollapsed: boolean;
  sidebarPosition: 'left' | 'right';
  stickyHeader: boolean;
  contentWidth: 'fluid' | 'boxed';
  gridSpacing: number;
  dashboardDensity: 'compact' | 'comfortable' | 'spacious';
  navigationStyle: 'sidebar' | 'topbar';
  topbarVisible: boolean;
}

export interface DashboardSettings {
  landingPage: string;
  widgetVisibility: Record<string, boolean>;
  widgetOrder: string[];
  recentActivityPanel: boolean;
  quickActionShortcuts: string[];
  favoriteModules: string[];
  refreshIntervalSeconds: number;
}

export interface NotificationSettings {
  desktopNotifications: boolean;
  soundAlerts: boolean;
  emailNotifications: boolean;
  securityAlerts: boolean;
  taskReminders: boolean;
  approvalRequestAlerts: boolean;
  maintenanceNotifications: boolean;
}

export interface DataViewSettings {
  paginationSize: number;
  tableDensity: 'compact' | 'comfortable' | 'spacious';
  rememberFilters: boolean;
  rememberSorting: boolean;
  exportFormat: 'csv' | 'xlsx' | 'json';
  autoSaveFilters: boolean;
  stickyTableHeaders: boolean;
}

export interface AccessibilitySettings {
  highContrast: boolean;
  reducedMotion: boolean;
  keyboardNavigation: boolean;
  screenReaderSupport: boolean;
  largerText: boolean;
  focusIndicators: boolean;
}

export interface LocalizationSettings {
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  numberFormat: string;
}

export interface ProductivitySettings {
  autoLogoutMinutes: number;
  sessionTimeoutWarning: boolean;
  openLinksInNewTab: boolean;
  multiTabSync: boolean;
  autosaveDrafts: boolean;
  commandPaletteEnabled: boolean;
}

export interface AdminSettingsPayload {
  appearance: AppearanceSettings;
  layout: LayoutSettings;
  dashboard: DashboardSettings;
  notifications: NotificationSettings;
  dataView: DataViewSettings;
  accessibility: AccessibilitySettings;
  localization: LocalizationSettings;
  productivity: ProductivitySettings;
}

export type AdminSettingsCategory = keyof AdminSettingsPayload;

async function authedFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export const adminSettingsService = {
  getAll: () => authedFetch<AdminSettingsPayload>('/admin/preferences'),

  updateCategory: <C extends AdminSettingsCategory>(
    category: C,
    values: Partial<AdminSettingsPayload[C]>,
  ) =>
    authedFetch<AdminSettingsPayload[C]>(`/admin/preferences/${category}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),

  resetAll: () =>
    authedFetch<AdminSettingsPayload>('/admin/preferences/reset', {
      method: 'POST',
    }),

  resetCategory: <C extends AdminSettingsCategory>(category: C) =>
    authedFetch<AdminSettingsPayload[C]>(
      `/admin/preferences/${category}/reset`,
      { method: 'POST' },
    ),
};
